import React, { useEffect, useState } from 'react'
import { Container, Card, Row, Col, Form, Button } from 'react-bootstrap'
import ClockTimePicker from '../../../../GlobalModules/Utility/Components/ClockTimePicker'
import { useParams, useHistory } from 'react-router-dom'
import { connect } from 'react-redux'
import * as BookingEventsController from '../Controller/BookingEventsController'
import Utility from '../../../../GlobalModules/Utility/Utility'

const EVENT_TYPES = [
  { value: 'BOOKING', label: 'Booking' },
  { value: 'EVENT', label: 'Event' },
  { value: 'PARTY', label: 'Party' },
  { value: 'MEETING', label: 'Meeting' }
]

const BookingEdit = (props) => {
  const { slack, lng } = useParams()
  const history = useHistory()

  const [form, setForm] = useState({
    event_type: 'BOOKING',
    start_date: '',
    start_time: '',
    end_date: '',
    end_time: '',
    name: '',
    email: '',
    phone: '',
    no_of_persons: '',
    description: ''
  })
  const [saving, setSaving] = useState(false)
  const [loading, setLoading] = useState(true)
  const [errors, setErrors] = useState({})

  useEffect(() => {
    const load = async () => {
      try {
        setLoading(true)
        const res = await BookingEventsController.getBookingDetailApi(slack)
        if (res.success && res.data) {
          const d = res.data
          // split datetime into date and time for inputs when available
          const sd =
            d.start_date && d.start_date !== '-'
              ? d.start_date.split(' ')[0]
              : ''
          const st = Utility.extractTimeFromDateTime(d.start_date)
          const ed =
            d.end_date && d.end_date !== '-' ? d.end_date.split(' ')[0] : ''
          const et = Utility.extractTimeFromDateTime(d.end_date)
          const to24Hour = (ampmStr) => {
            if (!ampmStr) return ''
            const [time, ampm] = ampmStr.split(' ')
            const [hhStr, mmStr] = time.split(':')
            let hh = parseInt(hhStr || '0', 10)
            const mm = parseInt(mmStr || '0', 10)
            const isPM = (ampm || '').toUpperCase() === 'PM'
            if (isPM && hh < 12) hh += 12
            if (!isPM && hh === 12) hh = 0
            return `${String(hh).padStart(2, '0')}:${String(mm).padStart(
              2,
              '0'
            )}`
          }
          setForm({
            event_type: d.event_type || 'BOOKING',
            start_date: sd,
            start_time: st,
            start_time_24: to24Hour(st),
            end_date: ed,
            end_time: et,
            end_time_24: to24Hour(et),
            name: d.name || '',
            email: d.email || '',
            phone: d.phone || '',
            no_of_persons: d.no_of_persons || '',
            description: d.description || ''
          })
        }
      } finally {
        setLoading(false)
      }
    }
    load()
  }, [slack])

  const clearError = (field) => {
    setErrors((prev) => {
      const newErrors = { ...prev }
      delete newErrors[field]
      return newErrors
    })
  }

  const onChange = (e) => {
    const { name, value } = e.target
    clearError(name)
    setForm((p) => ({ ...p, [name]: value }))
  }

  const validate = () => {
    const e = {}
    // Removed validation for name, email, phone, no_of_persons as requested
    if (!form.start_date) e.start_date = 'Start date is required'
    if (!form.end_date) e.end_date = 'End date is required'
    if (!form.start_time_24) e.start_time = 'Start time is required'
    if (!form.end_time_24) e.end_time = 'End time is required'
    setErrors(e)
    return Object.keys(e).length === 0
  }

  const onSubmit = async () => {
    if (!validate()) return
    try {
      setSaving(true)
      const { start_time_24, end_time_24, ...payload } = form
      if (!payload.start_time && start_time_24)
        payload.start_time = to12Hour(start_time_24)
      if (!payload.end_time && end_time_24)
        payload.end_time = to12Hour(end_time_24)
      // Fix: Send "0" if no_of_persons is blank
      if (!payload.no_of_persons || payload.no_of_persons === '') {
        payload.no_of_persons = '0'
      }
      const res = await BookingEventsController.updateBookingApi(slack, payload)
      if (res?.success) {
        Utility.toastNotifications(
          'Booking updated successfully',
          'Success',
          'success'
        )
        history.push(`/${lng}/booking-events`)
      } else {
        Utility.toastNotifications(
          res?.message || 'Failed to update booking',
          'Error',
          'error'
        )
      }
    } catch (error) {
      Utility.toastNotifications(
        error?.response?.data?.message ||
          error?.message ||
          'Failed to update booking',
        'Error',
        'error'
      )
    } finally {
      setSaving(false)
    }
  }

  const to12Hour = (hhmm) => {
    if (!hhmm) return ''
    const [hStr, mStr] = hhmm.split(':')
    let h = parseInt(hStr || '0', 10)
    const m = parseInt(mStr || '0', 10)
    const ampm = h >= 12 ? 'PM' : 'AM'
    h = h % 12
    if (h === 0) h = 12
    return `${h}:${m < 10 ? '0' : ''}${m} ${ampm}`
  }

  return (
    <Container fluid className='p-0'>
      <div className='d-flex justify-content-between align-items-center top-header-box'>
        <h4>Edit Booking or Event</h4>
        <Button onClick={onSubmit} disabled={saving}>
          {saving ? 'Saving...' : 'Save'}
        </Button>
      </div>

      <Card>
        <Card.Body>
          <Row className='g-3'>
            <Col md={4}>
              <Form.Label>Event Type</Form.Label>
              <Form.Select
                name='event_type'
                value={form.event_type}
                onChange={onChange}
              >
                {EVENT_TYPES.map((t) => (
                  <option key={t.value} value={t.value}>
                    {t.label}
                  </option>
                ))}
              </Form.Select>
            </Col>
          </Row>

          <Row className='g-3 mt-1'>
            <Col md={4}>
              <Form.Label>Booking or Event Start Date</Form.Label>
              <Form.Control
                type='date'
                name='start_date'
                value={form.start_date}
                onChange={onChange}
                min={(function() {
                  const n = new Date()
                  return n.toISOString().split('T')[0]
                })()}
                isInvalid={!!errors.start_date}
              />
              <Form.Control.Feedback type='invalid'>
                {errors.start_date}
              </Form.Control.Feedback>
            </Col>
            <Col md={4}>
              <Form.Label>Booking or Event Start Time</Form.Label>
              <ClockTimePicker
                value24={form.start_time_24}
                onChange={(val24) => {
                  setForm((p) => ({
                    ...p,
                    start_time_24: val24,
                    start_time: to12Hour(val24)
                  }))
                }}
                placeholder='Please enter booking/event start time'
              />
            </Col>
          </Row>

          <Row className='g-3 mt-1'>
            <Col md={4}>
              <Form.Label>Booking or Event End Date</Form.Label>
              <Form.Control
                type='date'
                name='end_date'
                value={form.end_date}
                onChange={onChange}
                min={(function() {
                  const now = new Date()
                  const todayStr = now.toISOString().split('T')[0]
                  if (form.start_date) return form.start_date
                  return todayStr
                })()}
                isInvalid={!!errors.end_date}
              />
              <Form.Control.Feedback type='invalid'>
                {errors.end_date}
              </Form.Control.Feedback>
            </Col>
            <Col md={4}>
              <Form.Label>Booking or Event End Time</Form.Label>
              <ClockTimePicker
                value24={form.end_time_24}
                min24={(function() {
                  const now = new Date()
                  const todayStr = now.toISOString().split('T')[0]
                  const hh = String(now.getHours()).padStart(2, '0')
                  const mm = String(now.getMinutes()).padStart(2, '0')
                  if (
                    form.end_date &&
                    form.start_date &&
                    form.end_date === form.start_date &&
                    form.start_time_24
                  ) {
                    // Same day: end time cannot be earlier than start time (equal allowed)
                    return form.start_time_24
                  }
                  return undefined
                })()}
                onChange={(val24) => {
                  setForm((p) => ({
                    ...p,
                    end_time_24: val24,
                    end_time: to12Hour(val24)
                  }))
                }}
                placeholder='Please enter booking/event end time'
              />
            </Col>
          </Row>

          <Row className='g-3 mt-1'>
            <Col md={4}>
              <Form.Label>Name</Form.Label>
              <Form.Control name='name' value={form.name} onChange={onChange} />
            </Col>
            <Col md={4}>
              <Form.Label>Email</Form.Label>
              <Form.Control
                name='email'
                value={form.email}
                onChange={onChange}
              />
            </Col>
            <Col md={4}>
              <Form.Label>Contact No</Form.Label>
              <Form.Control
                name='phone'
                value={form.phone}
                // onChange={onChange}
                onChange={(e) => {
                  Utility.handlePhoneInput(e);
                  onChange(e);
                }}
                minLength="10"
                maxLength="15"
              />
            </Col>
          </Row>

          <Row className='g-3 mt-1'>
            <Col md={4}>
              <Form.Label>No of Persons</Form.Label>
              <Form.Control
                name='no_of_persons'
                value={form.no_of_persons}
                // onChange={onChange}
                // type='number'
                // min='0'
                onChange={(e) => {
                  Utility.handlePhoneInput(e);
                  onChange(e);
                }}
              />
            </Col>
          </Row>

          <Row className='g-3 mt-1'>
            <Col md={8}>
              <Form.Label>Description</Form.Label>
              <Form.Control
                as='textarea'
                rows={5}
                name='description'
                value={form.description}
                onChange={onChange}
                placeholder='Enter description (optional)'
              />
            </Col>
          </Row>
        </Card.Body>
      </Card>
    </Container>
  )
}

const mapStateToProps = (globalState) => ({
  userCredentials: globalState.LoginReducer.userCredentials
})

export default connect(mapStateToProps)(BookingEdit)
